/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-depth */
import type { IContent } from '~/features-shared/content/interfaces/content'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { getTagsForContent } from '~/features-shared/tags/composables/tag-refs'
import type {
  ITag,
  ITagResponse,
  ITaggable,
  ITaggerFilter,
  TagArray,
  TagObject,
  TagResult,
  TagResultType,
  TaggingActionsSet,
} from '~/features-shared/tags/interfaces/tags'
import { isDeepEqual } from '~/features-shared/tags/utils/deep-equal'

// Constants used in this file
export const ISO_DATE_REGEXP = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.(\d{3}))?Z/
export const TYPE_PREFIX = 'xsd:'
export const TYPE_REG_EXP = /^xsd:/
export const SIXW_TAGS_NAMESPACE = 'ds6w'
export const ENCODED_SLASH = '&#47;'
export const DEFAULT_PREDICATE = 'ds6w:what'

/**
 * The function recursively calculates the tag objects based on groups and metas of an exalead response
 *
 * @param resultingSixWTags Cumulative array of tags
 * @param category category of the group of tags
 * @param rootPath root property of the group
 * @param rootPathLevelsCount the depth of the heirarchy of the group
 * @returns The array of tags
 */
export function pushSubjectTagsRecursively(
  resultingSixWTags: ITag[],
  category: any,
  rootPath: any,
  rootPathLevelsCount: number,
) {
  const subCategories = category.categories

  // checks whether there is a single value in this category or not
  if (subCategories && subCategories.length > 0) {
    // push all eventual sub-categories
    for (let k = 0, len = subCategories.length; k < len; k++) {
      resultingSixWTags = pushSubjectTagsRecursively(
        resultingSixWTags,
        subCategories[k],
        rootPath,
        rootPathLevelsCount,
      )
    }
    return resultingSixWTags
  }

  const predicateParts = []
  const fullPathParts = category.fullPath.split('/')

  // construct the predicate
  // while looking for the category of the tag (implicit/explicit)
  let tagNatureIndex = -1

  for (let k = 2, len = fullPathParts.length; k < len; k++) {
    const pathPart = fullPathParts[k]

    switch (pathPart) {
      case 'implicit':
      /* falls through */
      case 'explicit':
        tagNatureIndex = k
        break
      default:
        predicateParts.push(pathPart)
        break
    }
    if (tagNatureIndex > 0) {
      // stop the loop
      break
    }
  }
  const predicate = predicateParts.join('/')
  let tagType = fullPathParts[tagNatureIndex + 1]

  let litteral: any[] | string
  if (rootPathLevelsCount > tagNatureIndex + 1) {
    // the root path already contains the tag type
    litteral = category.fullPath.replace(`${rootPath}/`, '')
      .split('/')
  }
  else {
    // the root path does not contain the tag type
    litteral = fullPathParts.splice(tagNatureIndex + 2)
  }

  if (typeof tagType === 'string') {
    /* strip the 'xsd:' prefix */
    tagType = tagType.replace(TYPE_REG_EXP, '')
    if (tagType === 'date') {
      litteral = (litteral as any[]).join('/')
    }
    else {
      (litteral as any[]).forEach((val: string, idx: number) => {
        (litteral as any[])[idx] = val.replace(/&#47;/g, '/')
      })
      // IR-439013
      if (litteral.length === 1)
        litteral = litteral[0]
    }
  }

  resultingSixWTags.push({
    sixw: predicate,
    object: litteral,
    type: tagType,
    count: Number.parseInt(category.count, 10),
  })
  return resultingSixWTags
}

/**
 * This function parses the "groups" property and extracts tags from it
 *
 * @param groups Array of objects received in the groups property of an exalead response
 * @returns parsed tags
 */
export function extractTagsFromExaleadResponse(groups: any[]) {
  let sixwTags: ITag[] = []

  for (let i = 0, j = groups.length; i < j; i++) {
    const grp = groups[i]
    if (grp.id.substring(0, 4) === SIXW_TAGS_NAMESPACE)
      sixwTags = pushSubjectTagsRecursively(sixwTags, grp, grp.root, grp.root.split('/').length)
  }
  return sixwTags
}

/**
 * Function to parse tags not from an explicit exalead call
 *
 * @param tags Array of results
 * @returns Parsed tags
 */
export function parseTags(tags: ITagResponse[] = []) {
  const result: ITag[] = []

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    const metas = tag.metas
    const data: any = { source: tag.source }

    for (let j = 0; j < metas.length; j++) {
      const meta = metas[j]
      data[meta.name] = meta.value
    }
    result.push(data)
  }

  return result
}

/**
 * Function to evaluate if two tag objects are the same.
 *
 * @description Two tag objects are same if
 *
 * (a) they have the same predicate/sixw.
 * (b) they have the same value.
 *
 * Tag values can be of different types. So we have to check the type and
 * if they are same, then check the values.
 * If they are both arrays, then we have to check each element of the array as well
 *
 * @param aTag The first tag
 * @param bTag The second tag
 * @returns true if they are the same tag
 */
export function areTagsSame(aTag: Pick<ITag, 'sixw' | 'object'>, bTag: Pick<ITag, 'sixw' | 'object'>) {
  // Different predicates mean different tags
  if (aTag.sixw !== bTag.sixw)
    return false

  // Different object types (string/array) means different tags
  if (typeof aTag.object !== typeof bTag.object)
    return false

  if (aTag.object === bTag.object) {
    // They can be equal if both of them are strings
    return true
  }

  if (Array.isArray(aTag.object)) {
    // bTag is also array
    if (aTag.object.length !== bTag.object.length)
      return false

    for (let i = 0; i < aTag.object.length; i++) {
      if (aTag.object[i] !== bTag.object[i])
        return false
    }

    // Both are arrays and both have same items
    return true
  }

  // We're not sure yet?
  return false
}

/**
 * Function to evaluate if the filter objects of the TagNavigator are same or not.
 * Since they can be deeply nested, we have to have a deep equality check for the objects
 *
 * @param f1 The first TagNavigator filter
 * @param f2 The second TagNavigator filter
 * @returns true, if they are the same filters
 */
export function areFiltersSame(f1: ITaggerFilter['allfilters'], f2: ITaggerFilter['allfilters']) {
  return isDeepEqual(f1, f2)
}

/**
 * Function that converts the colons and slashes in a predicate string to exalead-friendly strings
 *
 * @param predicate The predicate string
 * @returns The string without colons and slashes
 */
export function getPredicateWithoutColonsAndSlashes(predicate: string) {
  return predicate.replace(/:/g, '_58_')
    .replace(/\//g, '_47_')
}

/**
 * Function that converts a TagNavigator filter to query string and refine params, that can be used to make
 * API calls to the backend.
 *
 * @param tagNavFilters The TagNavigator filter object
 * @returns query and refine params evaluated from the filters
 */
export function reconstructExaleadFacetsIds(tagNavFilters: ITaggerFilter['allfilters']) {
  let queryPart = ''
  const refineParam = []

  if (tagNavFilters) {
    for (const predicate in tagNavFilters) {
      if (Object.hasOwnProperty.call(tagNavFilters, predicate)) {
        const len = tagNavFilters[predicate].length

        for (let j = 0; j < len; j++) {
          const tagNavFilter = tagNavFilters[predicate][j]
          const initialType = (tagNavFilter as any).type
          let { object } = tagNavFilter as any
          const type = TYPE_PREFIX + initialType

          // encode the slashed that are contained in Exalead categories into "&#47;" again, before filtering
          if (initialType !== 'date') {
            if (Array.isArray(object)) {
              // for hierarchical tags
              object.forEach((val, idx) => {
                object[idx] = val.replaceAll(/\//g, ENCODED_SLASH)
              })
              object = object.join('/')
            }
            else {
              // for simple tags
              object = object.replaceAll(/\//g, ENCODED_SLASH)
            }
          }
          else if (predicate.indexOf('sixw:ds6w:when/') === 0 && typeof object === 'object') {
            if (object.from) {
              queryPart
                += `${queryPart !== '' ? ' AND ' : ''
                }(${getPredicateWithoutColonsAndSlashes(predicate.replace('sixw:ds6w:when/', ''))
                }>=${object.from
                })`
            }
            if (object.to) {
              queryPart
                += `${queryPart !== '' ? ' AND ' : ''
                }(${getPredicateWithoutColonsAndSlashes(predicate.replace('sixw:ds6w:when/', ''))
                }<=${object.to
                })`
            }
            // stop here; the "refineParam" array should not be filled
            continue
          }
          refineParam.push(
            `f/${SIXW_TAGS_NAMESPACE
            }${getPredicateWithoutColonsAndSlashes(`/${predicate}`)
            }/${type
            }/${object}`,
          )
        }
      }
    }
  }

  return { queryPart, refineParam }
}

/**
 * Function that converts a TagNavigator's allfilters property to query string and
 * refine params, that can be used to make API calls to the backend.
 *
 * @param filter The TagNavigator filter
 * @returns query and refine params evaluated from the filters
 */
export function reformatFilters(filter: ITaggerFilter) {
  let noFilter = true

  for (const i in filter.allfilters) {
    if (Object.hasOwnProperty.call(filter.allfilters, i)) {
      noFilter = false
      break
    }
  }

  if (noFilter === false)
    return reconstructExaleadFacetsIds(filter.allfilters)

  return {
    queryPart: '',
    refineParam: [],
  }
}

/**
 * Converts the passed date string to milliseconds
 *
 * @param dateString The string to convert to
 * @returns The date in milliseconds
 */
function getDateInMilliSec(dateString: string) {
  let extramilliseconds = 0
  // Avoid to fail on date.charAt
  if (!dateString)
    return null

  // Bug fix: handle Exalead date format too
  if (typeof (dateString) === 'string' && dateString.match(/(\/|&#x2F;)/g)) {
    dateString = dateString.replace('-', ' ')
      .replace(/(\/|&#x2F;)/g, '-')
  }

  const match = dateString.match(ISO_DATE_REGEXP)
  if (match) {
    dateString = dateString.replace('T', ' ')
      .replace(/.\d{3}Z$/, '')
    extramilliseconds = Number.parseInt(match[match.length - 1], 10)
  }
  if (dateString.substring(4, 5) !== '-'
    || dateString.substring(7, 8) !== '-'
    || dateString.substring(10, 11) !== ' '
    || dateString.substring(13, 14) !== ':'
    || dateString.substring(16, 17) !== ':')
    return null

  const dateSplited = dateString.split(' ')
  const dayData = dateSplited[0].split('-')
  const hourData = dateSplited[1].split(':')

  const date = new Date()
  date.setFullYear(+dayData[0])
  date.setMonth(+dayData[1] - 1) // -1 because in js, months belong to [0, 11]
  date.setDate(+dayData[2])
  date.setHours(+hourData[0])
  date.setMinutes(+hourData[1])
  date.setSeconds(+hourData[2])
  date.setMilliseconds(extramilliseconds)

  return date.getTime()
}

/**
 * Function that processes and returns tags for a content
 *
 * @param subjectURI subject uri of the content
 * @param tagSet The array of tags
 * @param result The tag object from response
 * @param resultType The expected output format
 * @returns Array or Object of tags
 */
export function processTagAttribute(subjectURI: SUBJECT_6W_URI, tagSet: string | any[], result: TagResult, resultType: TagResultType) {
  let i
  let len
  let type
  let tagValue
  let predicate
  let tagObj
  let finalTag

  resultType = resultType === 'array' ? 'array' : 'object'

  if (subjectURI) {
    if (resultType === 'array') {
      result = result || []
    }
    else {
      result = (result || {}) as TagObject
      result[subjectURI] = result[subjectURI] || []
    }

    for (i = 0, len = tagSet.length; i < len; i++) {
      tagObj = tagSet[i]
      if (tagObj.predicate)
        predicate = tagObj.predicate

      else
        predicate = DEFAULT_PREDICATE

      type = typeof tagObj.type === 'string' ? tagObj.type.replace(TYPE_REG_EXP, '') : null

      if (type === 'date') {
        tagValue = new Date(getDateInMilliSec(tagObj.tag) as number)
        tagValue = [tagValue.getFullYear(), tagValue.getMonth() + 1, tagValue.getDate()].join('/')
        type = 'string'
      }
      else {
        if (Array.isArray(tagObj.tag)) {
          tagValue = tagObj.tag
        }
        else {
          // this special treatment splitting slash is very useful for hierarchical
          // tags (and it appears as such in the tagger)
          tagValue = String(tagObj.tag)
            .split('/')
        }
        tagValue.forEach((val: string, idx: number) => {
          tagValue[idx] = val.replace(/&#47;/g, '/')
        })
        // workaround for IR-398195
        if (tagValue.length === 1)
          tagValue = tagValue[0]
      }

      if (tagValue.length !== 0) {
        finalTag = {
          object: tagValue,
          // dispValue : tagDisplayValue,
          sixw: predicate,
          type,
          subjectURI,
        }

        if (resultType === 'array')
          (result as TagArray).push(finalTag)

        else
          (result as TagObject)[subjectURI].push(finalTag)
      }
    }
  }
  return result
}

/**
 * Function to derive tags from html string
 *
 * @param html The input string
 * @returns an array of tags
 */
export function get6WTagsFromHTML(html: string | HTMLElement = '') {
  const result: Record<string, Record<string, string | undefined | null>> = {}
  let wrapper

  if (typeof html === 'string') {
    wrapper = document.createElement('div')
    wrapper.innerHTML = html // TODO: Check if this is safe to do
  }
  else {
    wrapper = html
  }

  wrapper
    .querySelectorAll<HTMLAnchorElement>('a.s6m-tag-link[data-predicate]')
    .forEach((tag: HTMLAnchorElement) => {
      const { predicate, tagValue } = tag.dataset
      const literal = tagValue || tag.textContent // Prioritize tagValue
      const tagString = `${predicate}/${literal}`

      result[tagString] = {
        predicate,
        object: literal,
      }
    })

  return result
}

/**
 * Function that returns tag actions attribute that is to be sent to the backend
 * to persist tags.
 *
 * @param intialContent - The description of a content before edition
 * @param finalContent - The description after edition
 * @param content - Content
 *
 * @returns The tagging actions set
 */
export function deriveTagActionsFromContentChanges(intialContent: string, finalContent: string, content: IContent) {
  const taggable = getTagsForContent(content.subject_uri)

  // to detect tag addition or removal
  if (!finalContent || !taggable || !content)
    return {}

  // intialContent can be null if we come from a save as draft
  const inlineTagsBeforeChanges = intialContent ? get6WTagsFromHTML(intialContent) : {}
  const inlineTagsAfterChanges = get6WTagsFromHTML(finalContent)
  const explicitTagsBeforeChangesIndex = (content.tags6w || []).reduce((result, tag) => {
    result[`${tag.predicate}/${tag.tag}`] = {
      predicate: tag.predicate,
      object: tag.tag,
    }
    return result
  }, {})

  for (const tag in inlineTagsBeforeChanges) {
    if (Object.hasOwnProperty.call(inlineTagsBeforeChanges, tag)
      && !Object.hasOwnProperty.call(inlineTagsAfterChanges, tag)
    )
      taggable.removeTag?.(inlineTagsBeforeChanges[tag] as Record<string, string>, true)
  }
  for (const tag in inlineTagsAfterChanges) {
    if (Object.hasOwnProperty.call(inlineTagsAfterChanges, tag)) {
      if (
        !Object.hasOwnProperty.call(inlineTagsBeforeChanges, tag)
        || !Object.hasOwnProperty.call(explicitTagsBeforeChangesIndex, tag)
      )
        taggable.addTag?.(inlineTagsAfterChanges[tag] as Record<string, string>, true)
    }
  }

  return {
    taggingActions: taggable.taggingActions6w,
    tags6w: taggable.tagsToSendArray,
  }
}

/**
 * Function that returns tag actions attribute that is to be sent to the backend to persist tags for new contents
 *
 * @param content The description after edition
 * @param taggable The taggable dummy to handle tags' manipulation
 *
 * @returns The tagging actions set
 */
export function deriveTagActionsForNewContent(content: string, taggable: ITaggable): TaggingActionsSet {
  // intialContent can be null if we come from a save as draft
  const inlineTags = get6WTagsFromHTML(content)
  for (const tag in inlineTags) {
    if (Object.hasOwnProperty.call(inlineTags, tag))
      taggable.addTag?.(inlineTags[tag] as Record<string, string>, true)
  }

  return {
    taggingActions: taggable.taggingActions6w,
    tags6w: taggable.tagsToSendArray,
  }
}

function _asAction(t: Record<string, string | null | undefined>) {
  return { predicate: t.predicate, object: { literal: t.object } }
}

function _asItem(t: Record<string, string | null | undefined>) {
  return { predicate: t.predicate, tag: t.object }
}

export interface deriveTagActionsFromContentDiffParams {
  /** The content on top of which tags are to be evaluated. */
  contentBefore: string
  /** The content for which tags are to be evaluated. */
  contentAfter: string
  /** The taggable (dummy/real) to handle tags' manipulation. */
  taggable: ITaggable
}

/**
 * Function that returns tag actions attribute that is to be sent to the backend to persist tags for diff of contents
 *
 * @param options Options to be sent
 * @param options.contentBefore The content on top of which tags are to be evaluated
 * @param options.contentAfter The content for which tags are to be evaluated
 * @param options.taggable The taggable (dummy/real) to handle tags' manipulation
 *
 * @returns the tagging actions set
 */
export function deriveTagActionsFromContentDiff({
  contentBefore,
  contentAfter,
  taggable,
}: deriveTagActionsFromContentDiffParams): TaggingActionsSet {
  const inlineTagsBeforeChanges = get6WTagsFromHTML(contentBefore)
  const inlineTagsAfterChanges = get6WTagsFromHTML(contentAfter)

  const tagsToAdd: Record<string, string | null | undefined>[] = []
  const tagsToRemove: Record<string, string | null | undefined>[] = []

  Object.keys(inlineTagsBeforeChanges)
    .forEach((tag) => {
      if (!Object.hasOwnProperty.call(inlineTagsAfterChanges, tag))
        tagsToRemove.push(inlineTagsBeforeChanges[tag])
    })

  Object.keys(inlineTagsAfterChanges)
    .forEach((tag) => {
      if (!Object.hasOwnProperty.call(inlineTagsBeforeChanges, tag))
        tagsToAdd.push(inlineTagsAfterChanges[tag])
    })

  return {
    taggingActions: {
      add: [{
        subject: unref(taggable.sixwURI),
        tag: tagsToAdd.map(_asAction)
          .concat([...taggable.taggingActions6w.add[0].tag]),
      }],
      remove: [{
        subject: unref(taggable.sixwURI),
        tag: tagsToRemove.map(_asAction)
          .concat([...taggable.taggingActions6w.remove[0].tag]),
      }],
    },
    tags6w: {
      add: tagsToAdd.map(_asItem)
        .concat([...taggable.tagsToSendArray?.add]),
      remove: tagsToRemove.map(_asItem)
        .concat([...taggable.tagsToSendArray?.remove]),
    },
  }
}

export function filterTaggingActionsSet({
  taggingActions,
  tags6w,
}: TaggingActionsSet): TaggingActionsSet {
  const sendTagActions: number | undefined = taggingActions?.add[0].tag.length
    || taggingActions?.remove[0].tag.length

  const sendTags: number | undefined = tags6w?.add.length || tags6w?.remove.length

  return {
    ...(sendTagActions ? { taggingActions } : undefined),
    ...(sendTags ? { tags6w } : undefined),
  }
}
